<template>
  <div>
    <SmallModal
      :is-open="open"
      @close="close"
    >
      <template #header>
        <div class="flex justify-between items-center">
          Confirm Check-in
          <Menu
            as="div"
            class="relative inline-block text-left"
          >
            <div v-if="selectedLocation">
              <MenuButton
                class="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 hover:bg-gray-300"
              >
                {{ selectedLocation.name }}
                <ChevronDownIcon
                  class="-mr-1 ml-2 h-5 w-5"
                  aria-hidden="true"
                />
              </MenuButton>
            </div>

            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="opacity-0 scale-95"
              enter-to-class="opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="opacity-100 scale-100"
              leave-to-class="opacity-0 scale-95"
            >
              <MenuItems
                class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-gray-100 divide-y divide-gray-400 focus:outline-none z-50"
              >
                <div class="py-1">
                  <MenuItem
                    v-for="location in locations"
                    v-slot="{ active }"
                  >
                    <a
                      @click="selectLocation(location)"
                      href="#"
                      :class="[
                        active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                        'group flex items-center px-4 py-2 text-sm hover:bg-blue-600 hover:text-gray-100'
                      ]"
                    >
                      {{ location.name }}
                    </a>
                  </MenuItem>
                </div>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </template>

      <div
        v-if="customer"
        class="mt-4"
      >
        <div class="flex items-center">
          <div class="mr-5">
            <UserAddIcon
              class="p-3 rounded-lg border border-gray-400 text-blue-600 h-14 w-14"
              aria-hidden="true"
            />
          </div>
          <div class="leading-tight">
            <div class="text-blue-600 text-lg font-semibold leading-tight">
              {{ customer.name }}
            </div>
            <div
              v-if="['disabled', 'banned'].includes(customer.status)"
              class="text-red-400 font-semibold leading-tight"
            >
              {{ capitalize(customer.status) }}
            </div>
            <div class="text-gray-600 font-semibold leading-tight">
              Patient #: {{ customer.patient_number }}
              <br />
              DOB: {{ customer.birthdate ? _date(customer.birthdate) : '-' }}
            </div>
            <div
              v-if="customer.notes"
              class="text-gray-600 font-semibold leading-tight"
            >
              Notes: {{ customer.notes }}
            </div>
          </div>
        </div>

        <p
          v-if="getErrors('customer', checkInForm)"
          class="input-error"
        >
          {{ getErrors('customer', checkInForm) }}
        </p>
      </div>

      <template #footer>
        <button
          @click.prevent="confirm()"
          :disabled="checkingIn"
          type="button"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
        >
          {{ confirmText }}
        </button>

        <button
          @click.prevent="edit()"
          :disabled="checkingIn"
          type="button"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 ml-2"
        >
          Edit + Check-In
        </button>

        <button
          @click="close()"
          type="button"
          class="mt-3 inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-gray-100 font-medium text-gray-700 shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 sm:mt-0 ml-2 w-auto text-sm"
        >
          Cancel
        </button>
      </template>
    </SmallModal>
  </div>
</template>

<script>
import SmallModal from '@/Components/Modals/SmallModal.vue'
import { PlusSmIcon as PlusSmIconSolid, UsersIcon, UserAddIcon, ChevronDownIcon } from '@heroicons/vue/solid'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import interactsWithCurrentLocation from '@/Mixins/interactsWithCurrentLocation'
import getsErrors from '@/Mixins/getsErrors'
import checksInCustomers from '@/Mixins/checksInCustomers'
import _date from '@/Filters/Date'
import capitalize from '../../../Filters/Capitalize'

export default {
  props: {
    open: {
      type: Boolean,
      default: false
    },
    customer: {
      type: Object,
      default() {
        return null
      }
    },
    /**
     * Location ID to pre-select
     */
    locationId: {
      type: Number,
      default: null
    }
  },

  emits: ['update:open'],

  mixins: [interactsWithCurrentLocation, checksInCustomers, getsErrors],

  components: {
    SmallModal,
    PlusSmIconSolid,
    UsersIcon,
    UserAddIcon,
    ChevronDownIcon,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems
  },

  data() {
    return {
      selectedLocationId: null,
      checkingIn: false
    }
  },

  created() {
    this.setInitialSelection()
  },

  computed: {
    selectedLocation() {
      if (!this.selectedLocationId) {
        return null
      }

      return this.locations.find((location) => location.id === this.selectedLocationId)
    },
    confirmText() {
      return this.checkingIn ? 'Checking In...' : 'Check-In'
    },
    errors() {
      return this.$page.props.errors.checkInCustomer || {}
    }
  },

  watch: {
    open(newVal, oldVal) {
      if (newVal) {
        this.checkingIn = false
        this.setInitialSelection()
      }
    },
    selectedLocationId(newVal, oldVal) {
      this.checkInForm.location_id = newVal || null
    },
    customer(newVal, oldVal) {
      this.checkInForm.clearErrors()
    }
  },

  methods: {
    capitalize,
    _date,
    setInitialSelection() {
      this.selectedLocationId = this.locationId || this.currentLocationId
      this.checkInForm.location_id = this.selectedLocationId
    },
    close() {
      this.$emit('update:open', false)
    },
    selectLocation(location) {
      this.selectedLocationId = location.id
    },
    edit() {
      this.close()
      this.$emit('edit', this.customer)
    },
    confirm() {
      this.checkingIn = true

      this.checkInCustomer(
        this.customer.id,
        () => {
          this.close()
          this.checkingIn = false
          this.$emit('complete')
        },
        () => {
          this.checkingIn = false
        }
      )
    }
  }
}
</script>
